/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-panel/index.tsx company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import { pick } from 'lodash';
import moment from 'moment/moment';
import { getPanelInitialState } from '@components/central-monitoring/panels/add-panel/add-panel.data';
import { AddPanelDialogProps } from '@components/central-monitoring/panels/add-panel/add-panel.interface';
import Step4Overview from '@components/central-monitoring/panels/add-panel/step4-overview';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import Step2Donut from './step2-donut';
import { IPanelExtended } from './step4-overview/step4-overview.interface';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import Step2StackedColumn from './step2-stacked-column';
import { checkStep4Availability } from './step4-overview/step4-overview.helper';
import { useAuthorization, useBoolean, useTranslation, useEnhancedReducer } from 'hooks';
import { GET_PANEL } from 'queries/central-monitoring';
import { PanelFields_Panel_graph_PanelGraph_series_PanelGraphSeries as PanelFields_graph_series } from 'queries/generated/fragments.generated';
import { GET_STUDY_VISIT_SCHEDULES } from 'queries/study-visit-schedules';
import {
    PanelDisplayBy,
    PanelDisplayUnits,
    PanelGraphType,
    PanelLineTimePeriod,
    PanelSeriesType,
    PanelType,
    PanelOrderBy,
    PanelColumnType,
    PanelLineCalculation,
    PanelAverageLineThresholdType,
} from 'types/graphql-global-types';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import Step1 from 'views/components/central-monitoring/panels/add-panel/step1-content';
import Step2Column from 'views/components/central-monitoring/panels/add-panel/step2-column';
import Step2FunnelModel from 'views/components/central-monitoring/panels/add-panel/step2-funnel-model';
import Step2FunnelTrendModel from 'views/components/central-monitoring/panels/add-panel/step2-funnel-trend-model';
import Step2HeatMap from 'views/components/central-monitoring/panels/add-panel/step2-heat-map';
import Step2Line from 'views/components/central-monitoring/panels/add-panel/step2-line-content';
import Step2NegativeBinomial from 'views/components/central-monitoring/panels/add-panel/step2-negative-binomial';
import Step2PoissonTrend from 'views/components/central-monitoring/panels/add-panel/step2-poisson-trend';
import Step2TriOperationalTrendModel from 'views/components/central-monitoring/panels/add-panel/step2-tri-operation-trend';
import Step2TriOperationalModel from 'views/components/central-monitoring/panels/add-panel/step2-tri-operational-model';
import Step3Preview from 'views/components/central-monitoring/panels/add-panel/step3-preview';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogActions, DialogPage } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import ReasonForChangeDialog from 'views/components/core/reason-change-dialog';

import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import StepsBox from 'views/components/core/steps-box';
import Constants from '@utils/constants';
import { panel, panelVariables } from '@queries/generated/central-monitoring.generated';
import { studyVisitSchedules, studyVisitSchedulesVariables } from '@queries/generated/study-visit-schedules.generated';

const { modelOptions, pageNumber } = PanelConstants;

const AddPanelDialog = ({
    open,
    onClose,
    studyId,
    currentPanel,
    panels,
    canSave = true,
    dashboardType,
    siteId,
}: AddPanelDialogProps) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);

    const [loadPanel, { loading, data: panelData }] = useLazyQuery<panel, panelVariables>(GET_PANEL);
    const [loadSchedules, { data: schedulesData }] = useLazyQuery<studyVisitSchedules, studyVisitSchedulesVariables>(
        GET_STUDY_VISIT_SCHEDULES,
    );

    const pageTitles = [t('admin:panel1'), t('admin:setup2'), t('admin:preview3'), t('admin:overview4')];
    const initialState = getPanelInitialState(studyId, user.organisationId);

    const firstStepRef = useRef(null);
    const thirdStepRef = useRef(null);
    const fourthStepRef = useRef(null);
    const step2LineRef = useRef(null);
    const step2FunnelRef = useRef(null);
    const step2ColumnRef = useRef(null);
    const step2HeatMapRef = useRef(null);
    const step2NBRef = useRef(null);
    const step2PoissonTrendRef = useRef(null);
    const step2FunnelTrendRef = useRef(null);
    const step2TRI = useRef(null);
    const step2TRITrend = useRef(null);
    const step2DonutRef = useRef(null);
    const step2StackedColumnRef = useRef(null);

    const showWarningLostData = useBoolean(false);
    const enabledClose = useBoolean(true);
    const showReasonForChangeDialog = useBoolean(false);
    const disabledSave = useBoolean(false);

    const [currentDashboardType] = useState(dashboardType);
    const [errors, setErrors] = useState([]);
    const [page, setPage] = useState({
        number: pageNumber.step1Content,
        params: '',
        available: pageNumber.step1Content,
    } as DialogPage);
    const [reason, setReason] = useState<string>();
    const [state, updateState, setState] = useEnhancedReducer({ ...initialState });

    const comparePanelFields = (object1: any, object2: any) => {
        const ignore = ['modelParams.show', 'createdBy', 'created', 'updated', 'updatedBy', 'auditId', 'showStep4'];
        // check if changes in dialog were made
        const keys1 = Object.keys(object1);
        for (let i = 0; i < keys1.length; i++) {
            if (
                ignore.indexOf(keys1[i]) < 0 &&
                JSON.stringify(object1[keys1[i]]) !== JSON.stringify(object2[keys1[i]])
            ) {
                return false;
            }
        }
        return true;
    };

    const pageHasErrors = (): boolean =>
        errors[page.number] &&
        Object.keys(errors[page.number]).filter((item: any) => !!errors[page.number][item]).length > 0;

    useEffect(() => {
        if (!pageHasErrors()) {
            disabledSave.setFalse();
        }
    }, [state]);

    useEffect(() => {
        if (open)
            enabledClose.setValue(
                comparePanelFields(currentPanel && panelData?.panel ? panelData.panel : initialState, state),
            );
        loadSchedules({ variables: { studyId } });
    }, [panelData, state, open]);

    useEffect(() => {
        if (currentPanel) {
            loadPanel({ variables: { panelId: currentPanel.id } });
        } else {
            setState({ ...initialState });
        }
    }, [currentPanel]);

    const getScheduleOptions = (): SelectWithSearchDataArrayInterface[] => {
        const groupedSchedules = schedulesData?.studyVisitSchedules?.reduce((res: any[], curr: any) => {
            const exists = res.findIndex((x: any) => x.groupId === curr.groupId);
            if (exists < 0) res.push(curr);
            else if (moment(curr.versionDate?.time) < moment(res[exists].versionDate?.time)) res[exists] = { ...curr };
            return res;
        }, []);
        const scheduleOptions = groupedSchedules?.map((value) => ({
            id: value.id,
            text: value.name,
            active: value.active,
        }));
        scheduleOptions?.unshift({
            id: Constants.ObjectId.Empty,
            text: t('admin:selectScheduleNameR'),
            default: true,
        });
        return scheduleOptions || [];
    };

    useEffect(() => {
        if (open && panelData?.panel && currentPanel) {
            const newState: IPanelExtended = { ...panelData?.panel };
            newState.showStep4 = checkStep4Availability(newState);
            setState({ ...newState });
            const panelModel = PanelService.getPanelModel(newState.modelParams);
            // set selected Type and Model fields when Edit Panel is opened
            if (newState.panelType === PanelType.GRAPH && newState.graph.type === PanelGraphType.SCATTER_PLOT_LINE)
                setPage({ number: pageNumber.step1Content, params: 'line', available: pageNumber.step3Preview });
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.FUNNEL) {
                setPage({
                    number: pageNumber.step1Content,
                    params: 'funnelModel',
                    available: pageNumber.step4Overview,
                });
            }
            if (newState.panelType === PanelType.GRAPH && newState.graph.type === PanelGraphType.HEATMAP)
                setPage({ number: pageNumber.step1Content, params: 'heatMap', available: pageNumber.step3Preview });
            if (newState.panelType === PanelType.GRAPH && newState.graph.type === PanelGraphType.COLUMN)
                setPage({ number: pageNumber.step1Content, params: 'column', available: pageNumber.step3Preview });
            if (newState.panelType === PanelType.GRAPH && newState.graph.type === PanelGraphType.DONUT)
                setPage({ number: pageNumber.step1Content, params: 'donut', available: pageNumber.step3Preview });
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.FUNNEL_TREND)
                setPage({
                    number: pageNumber.step1Content,
                    params: 'funnelTrendModel',
                    available: pageNumber.step3Preview,
                });
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.NEGATIVE_BINOMIAL) {
                setPage({
                    number: pageNumber.step1Content,
                    params: 'negativeBinomial',
                    available: pageNumber.step4Overview,
                });
            }
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.RATE_GROUP) {
                setPage({
                    number: pageNumber.step1Content,
                    params: 'poissonModel',
                    available: pageNumber.step4Overview,
                });
            }
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.RATE_GROUP_TREND)
                setPage({
                    number: pageNumber.step1Content,
                    params: 'poissonTrend',
                    available: pageNumber.step3Preview,
                });
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.TRI_OPERATIONAL) {
                setPage({
                    number: pageNumber.step1Content,
                    params: 'triOperational',
                    available: pageNumber.step4Overview,
                });
            }
            if (newState.panelType === PanelType.MODEL && panelModel === modelOptions.TRI_OPERATIONAL_TREND)
                setPage({
                    number: pageNumber.step1Content,
                    params: 'triOperationalTrendModel',
                    available: pageNumber.step3Preview,
                });
            if (newState.panelType === PanelType.GRAPH && newState.graph.type === PanelGraphType.STACKED_COLUMN)
                setPage({
                    number: pageNumber.step1Content,
                    params: 'stackedColumn',
                    available: pageNumber.step3Preview,
                });
        }
    }, [panelData, open]);

    const getPanelFromState = () => {
        const panelState = {
            ...state,
            id: currentPanel?.id || null,
            weight: +state.weight,
            modelParams: {
                ...state.modelParams,
                hideUpper: state.modelParams.hideUpper,
                hideLower: state.modelParams.hideLower,
            },
            studyOverviewExtraColumns: state.studyOverviewExtraColumns,
            graph: {
                type: state.graph.type,
                plotTitle: state.graph.plotTitle,
                plotThreshold: state.graph.plotThreshold,
                xTitle: state.graph.xTitle,
                yTitle: state.graph.yTitle,
                displayBy: state.graph.displayBy,
                displayUnits: state.graph.displayUnits,
                hideStudyColumns: state.graph.hideStudyColumns,
                orderBy: state.graph.orderBy,
                columnType: state.graph.columnType,
                categories: state.graph.categories,
                timePeriod: state.graph.timePeriod,
                lineCalculation: state.graph.lineCalculation,
                lineThresholdType: state.graph.lineThresholdType,
                thresholdNum1: state.graph.thresholdNum1,
                thresholdNum2: state.graph.thresholdNum2,
                seriesType: state.graph.seriesType,
                numberOfSeries: state.graph.numberOfSeries,
                categoryRangeUnit: state.graph.categoryRangeUnit,
                categoryRangePeriod: state.graph.categoryRangePeriod,
                columnRangeCategory: state.graph.columnRangeCategory,
                dataInclusionUnit: state.graph.dataInclusionUnit,
                dataInclusionPeriod: state.graph.dataInclusionPeriod,
                series: [
                    {
                        label:
                            state.graph.series1label ??
                            (state.graph.series?.length > 0 ? state.graph.series[0].label : ''),
                        dataSource:
                            state.graph.series1dataSource ??
                            (state.graph.series?.length > 0 ? state.graph.series[0].dataSource : null),
                        dateField:
                            state.graph.series1dateField ??
                            (state.graph.series?.length > 0 ? state.graph.series[0].dateField : ''),
                        dataPointField:
                            state.graph.series1dataPointField ??
                            (state.graph.series?.length > 0 ? state.graph.series[0].dataPointField : ''),
                        threshold:
                            state.graph.series1threshold ??
                            (state.graph.series?.length > 0 ? state.graph.series[0].threshold : ''),
                    },
                    {
                        label:
                            state.graph.series2label ??
                            (state.graph.series?.length > 1 ? state.graph.series[1].label : ''),
                        dataSource:
                            state.graph.series2dataSource ??
                            (state.graph.series?.length > 1 ? state.graph.series[1].dataSource : null),
                        dateField:
                            state.graph.series2dateField ??
                            (state.graph.series?.length > 1 ? state.graph.series[1].dateField : ''),
                        dataPointField:
                            state.graph.series2dataPointField ??
                            (state.graph.series?.length > 1 ? state.graph.series[1].dataPointField : ''),
                        threshold:
                            state.graph.series2threshold ??
                            (state.graph.series?.length > 1 ? state.graph.series[1].threshold : ''),
                    },
                    {
                        label:
                            state.graph.series3label ??
                            (state.graph.series?.length > 2 ? state.graph.series[2].label : ''),
                        dataSource:
                            state.graph.series3dataSource ??
                            (state.graph.series?.length > 2 ? state.graph.series[2].dataSource : null),
                        dateField:
                            state.graph.series3dateField ??
                            (state.graph.series?.length > 2 ? state.graph.series[2].dateField : ''),
                        dataPointField:
                            state.graph.series3dataPointField ??
                            (state.graph.series?.length > 2 ? state.graph.series[2].dataPointField : ''),
                        threshold:
                            state.graph.series3threshold ??
                            (state.graph.series?.length > 2 ? state.graph.series[2].threshold : ''),
                    },
                    {
                        label:
                            state.graph.series4label ??
                            (state.graph.series?.length > 3 ? state.graph.series[3].label : ''),
                        dataSource:
                            state.graph.series4dataSource ??
                            (state.graph.series?.length > 3 ? state.graph.series[3].dataSource : null),
                        dateField:
                            state.graph.series4dateField ??
                            (state.graph.series?.length > 3 ? state.graph.series[3].dateField : ''),
                        dataPointField:
                            state.graph.series4dataPointField ??
                            (state.graph.series?.length > 3 ? state.graph.series[3].dataPointField : ''),
                        threshold:
                            state.graph.series4threshold ??
                            (state.graph.series?.length > 3 ? state.graph.series[3].threshold : ''),
                    },
                ],
            },
        };

        // Threshold is a nullable number but the UI controls don't like null as a value and it's confuses the code
        // creating the panel above when state.graph.seriesXthreshold is null it doesn't know which value to use. So,
        // use '' (empty string) instead of null everywhere and then just before we send the config to the backend
        // convert '' to null.
        for (let x = 0; x < panelState.graph.series?.length; x++) {
            if (panelState.graph.series[x].threshold === '') {
                panelState.graph.series[x].threshold = null;
            }
        }

        // Get fields that the graphql query want to see for PanelInput
        const panelInputFields = { ...pick(panelState, PanelConstants.panelInputFields) };
        delete panelInputFields.modelParams.show;

        // Taken from: https://stackoverflow.com/questions/52873741/apollo-boost-typename-in-query-prevent-new-mutation
        const stripTypenames = (value: any): any => {
            if (Array.isArray(value)) return value.map(stripTypenames);

            if (value !== null && typeof value === 'object') {
                const newObject: any = {};
                Object.keys(value).forEach((property: string) => {
                    if (property !== '__typename') newObject[property] = stripTypenames(value[property]);
                });

                return newObject;
            }

            return value;
        };

        return stripTypenames(panelInputFields);
    };

    const handleSecondSteps = (withoutNav?: boolean, number?: number) => {
        // trigger validation for the correct step with navigation on next step and without
        if (
            page.params === 'line' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2LineRef.current.nextButton(withoutNav);
        else if (
            page.params === 'column' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2ColumnRef.current.nextButton(withoutNav);
        else if (
            page.params === 'heatMap' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2HeatMapRef.current.nextButton(withoutNav);
        else if (
            page.params === 'funnelModel' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2FunnelRef.current.nextButton(withoutNav);
        else if (
            page.params === 'funnelTrendModel' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2FunnelTrendRef.current.nextButton(withoutNav);
        else if (
            page.params === 'poissonModel' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2NBRef.current.nextButton(withoutNav);
        // poissonModel uses same config as negativeBinomial model, so uses same step2Ref
        else if (
            page.params === 'poissonTrend' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2PoissonTrendRef.current.nextButton(withoutNav);
        else if (
            page.params === 'triOperational' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2TRI.current.nextButton(withoutNav);
        else if (
            page.params === 'triOperationalTrendModel' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2TRITrend.current.nextButton(withoutNav);
        else if (
            page.params === 'negativeBinomial' &&
            ((page.number === pageNumber.step2Setup && number === pageNumber.step3Preview) || withoutNav)
        )
            step2NBRef.current.nextButton(withoutNav);
        else if (
            page.params === 'donut' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2DonutRef.current.nextButton(withoutNav);
        else if (
            page.params === 'stackedColumn' &&
            ((number === pageNumber.step3Preview && page.number === pageNumber.step2Setup) || withoutNav)
        )
            step2StackedColumnRef.current.nextButton(withoutNav);
    };

    const clean2Step = (withoutState?: boolean, panelModel?: string) => {
        // clean data when another model/type for panel was selected
        setPage({ ...page, available: pageNumber.step1Content });
        if (!withoutState)
            setState({
                active: state.active,
                name: state.name,
                showStep4: checkStep4Availability(state),
                graph: {
                    ...state.graph,
                    displayBy: PanelDisplayBy.SITE,
                    displayUnits: PanelDisplayUnits.PERCENT,
                    hideStudyColumns: false,
                    orderBy: PanelOrderBy.LABEL_DESCENDING,
                    columnType: PanelColumnType.STANDARD,
                    categories: [] as any,
                    timePeriod: PanelLineTimePeriod.WEEKS,
                    seriesType: PanelSeriesType.SITE,
                    lineCalculation: PanelLineCalculation.MEAN,
                    lineThresholdType: PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
                    thresholdNum1: 0,
                    thresholdNum2: 0,
                    numberOfSeries: 1,
                    series: [] as any,
                    series1label: '',
                    series1dataSource: null,
                    series1dataPointField: '',
                    series1dateField: '',
                    series1threshold: '',
                    series2label: '',
                    series2dataSource: null,
                    series2dataPointField: '',
                    series2dateField: '',
                    series2threshold: '',
                    series3label: '',
                    series3dataSource: null,
                    series3dataPointField: '',
                    series3dateField: '',
                    series3threshold: '',
                    series4label: '',
                    series4dataSource: null,
                    series4dataPointField: '',
                    series4dateField: '',
                    series4threshold: '',
                },
                weight: state.weight,
                includeInSiteScoring: state.includeInSiteScoring,
                panelType: state.panelType,
                decision: state.decision,
                modelParams: {
                    ...initialState.modelParams,
                    type: panelModel || null,
                },
                dataSource: {
                    ...initialState.dataSource,
                },
            });
        if (page.params === 'line') step2LineRef.current.cleanStep();
        if (page.params === 'funnelModel') step2FunnelRef.current.cleanStep();
        if (page.params === 'funnelTrendModel') step2FunnelTrendRef.current.cleanStep();
        if (page.params === 'poissonTrend') step2PoissonTrendRef.current.cleanStep();
        if (page.params === 'triOperational') step2TRI.current.cleanStep();
        if (page.params === 'triOperationalTrendModel') step2TRITrend.current.cleanStep();
        // poissonModel uses same config as negativeBinomial model, so uses same step2Ref
        if (page.params === 'negativeBinomial' || page.params === 'poissonModel') step2NBRef.current.cleanStep();
        if (page.params === 'column') step2ColumnRef.current.cleanStep();
        if (page.params === 'donut') step2DonutRef.current.cleanStep();
        if (page.params === 'stackedColumn') step2StackedColumnRef.current.cleanStep();
    };

    const validateFourthStage = () => fourthStepRef.current.validationOnUpdate();

    const validateSecondStage = () => {
        let isValid = false;
        if (page.number === pageNumber.step1Content) isValid = firstStepRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'column')
            isValid = step2ColumnRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'funnelModel')
            isValid = step2FunnelRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'funnelTrendModel')
            isValid = step2FunnelTrendRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'heatMap')
            isValid = step2HeatMapRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'line')
            isValid = step2LineRef.current.validationOnUpdate();
        else if (
            page.number === pageNumber.step2Setup &&
            (page.params === 'negativeBinomial' || page.params === 'poissonModel')
        )
            isValid = step2NBRef.current.validationOnUpdate();
        // poissonModel uses same config as negativeBinomial model, so uses same step2Ref
        else if (page.number === pageNumber.step2Setup && page.params === 'poissonTrend')
            isValid = step2PoissonTrendRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'triOperationalTrendModel')
            isValid = step2TRITrend.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'triOperational')
            isValid = step2TRI.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'donut')
            isValid = step2DonutRef.current.validationOnUpdate();
        else if (page.number === pageNumber.step2Setup && page.params === 'stackedColumn')
            isValid = step2StackedColumnRef.current.validationOnUpdate();
        else return true;

        return isValid;
    };

    const onChangeStep = (number: number, withoutNav?: boolean) => {
        if (isSysAdmin) return;

        if (validateSecondStage() && validateFourthStage()) {
            if (number === pageNumber.step2Setup && withoutNav) handleSecondSteps(withoutNav);
            else if (page.number === pageNumber.step2Setup && number === pageNumber.step3Preview)
                handleSecondSteps(false, number);
            else if (page.number === pageNumber.step1Content && number === pageNumber.step3Preview) {
                const value = firstStepRef.current.nextButton(true);
                if (value) onChangeStep(pageNumber.step2Setup, true);
            } else if (
                page.number === pageNumber.step1Content &&
                number === pageNumber.step2Setup &&
                firstStepRef.current
            )
                firstStepRef.current.nextButton();
            else {
                setPage({ ...page, number });
            }
        }
    };

    const onEditSave = () => {
        // save on 1 and 2 steps with validation in Edit Panel dialog
        if (validateSecondStage() && validateFourthStage()) thirdStepRef.current.onSaveStep();
    };

    const validateBackButton = () => {
        if (validateSecondStage() && validateFourthStage()) setPage({ ...page, number: page.number - 1 });
    };

    const close = () => {
        setPage({ number: pageNumber.step1Content, params: '', available: pageNumber.step1Content });
        setState({ ...initialState });
        onClose();
        setReason(null);
    };

    const updateErrorState = (errorState: any, errorPageNumber: number) => {
        const errorsState = [...errors];
        errorsState[errorPageNumber] = errorState;
        setErrors(errorsState);
    };

    const setGraphSeriesState = (seriesNumber: number, newSeries: PanelFields_graph_series) => {
        const newSeriesState = state.graph.series.map((series: PanelFields_graph_series, index: number) =>
            index + 1 === seriesNumber ? newSeries : series,
        );

        setState({
            ...state,
            graph: {
                ...state.graph,
                series: newSeriesState,
            },
        });
    };

    const clearGraphSeriesState = (fromSeriesNumber: number) => {
        const newSeriesState = state.graph.series.map((series: PanelFields_graph_series, index: number) =>
            index + 1 < fromSeriesNumber
                ? series
                : ({
                      __typename: 'PanelGraphSeries',
                      label: '',
                      dataSource: null,
                      dataPointField: '',
                      dateField: '',
                      threshold: null,
                  } as PanelFields_graph_series),
        );

        setState({
            ...state,
            graph: {
                ...state.graph,
                series: newSeriesState,
            },
        });
    };

    const getBackBtnText = (number: number) => {
        switch (number) {
            case pageNumber.step2Setup:
                return 'backToPanel';
            case pageNumber.step3Preview:
                return 'backToSetup';
            case pageNumber.step4Overview:
                return 'backToPreview';
            default:
                return '';
        }
    };

    const onNextSaveClick = () => {
        if (page.number === pageNumber.step1Content) {
            firstStepRef.current.nextButton();
        } else if (page.number === pageNumber.step2Setup) {
            handleSecondSteps(false, pageNumber.step3Preview);
        } else if (page.number === pageNumber.step3Preview && state.showStep4) {
            setPage({
                ...page,
                number: pageNumber.step4Overview,
                available: pageNumber.step4Overview,
            });
        } else if (page.number === pageNumber.step4Overview) {
            const isStepValid = fourthStepRef.current.validationOnUpdate();
            if (isStepValid && canSave) {
                thirdStepRef.current.onSaveStep();
            }
        } else if (canSave) {
            thirdStepRef.current.onSaveStep();
        }
    };

    const proceedBtnText = state.showStep4
        ? page.number < pageNumber.step4Overview
            ? 'nextButton'
            : 'save'
        : page.number < pageNumber.step3Preview
          ? 'nextButton'
          : 'save';

    const proceedBtnId = state.showStep4
        ? page.number < pageNumber.step4Overview
            ? 'next-button-index'
            : 'save-button'
        : page.number < pageNumber.step3Preview
          ? 'next-button-index'
          : 'save-button';

    if (loading) return <Loading float />;

    return (
        <>
            <ReasonForChangeDialog
                open={showReasonForChangeDialog.value}
                onClose={(value: string | undefined) => {
                    setReason(value);
                    showReasonForChangeDialog.setFalse();
                    if (value) thirdStepRef.current.onSaveStep(value);
                }}
            />
            <WarningLostDataDialog
                open={showWarningLostData.value}
                onClose={(action: string) => {
                    if (action === 'close') close();
                    showWarningLostData.setFalse();
                }}
            />
            <CustomDialog
                className="create-panel-dialog max-w700"
                onClose={() => {}}
                fullWidth
                maxWidth="md"
                scroll="paper"
                open={open && !showWarningLostData.value}
            >
                <DialogTitle id="add-library-control-modal-title">
                    {page.number === pageNumber.step1Content && !currentPanel && t('admin:addPanel')}
                    {page.number === pageNumber.step1Content &&
                        currentPanel &&
                        t('admin:editPanel', { id: currentPanel?.recordId ?? currentPanel?.id })}
                    {page.number === pageNumber.step2Setup && t('admin:setup2Name', { name: state.name })}
                    {page.number === pageNumber.step3Preview && t('admin:previewPanelName', { name: state.name })}
                    {page.number === pageNumber.step4Overview && t('admin:overviewPanelName', { name: state.name })}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            if (!enabledClose.value) showWarningLostData.setTrue();
                            else {
                                enabledClose.setTrue();
                                showWarningLostData.setFalse();
                                close();
                            }
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>

                <DialogContent
                    style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}
                    className="add-panel-dialog-content"
                >
                    <StepsBox page={page} onChangeStep={onChangeStep} pageTitles={pageTitles} />
                    <Step1
                        ref={firstStepRef}
                        page={page}
                        show={page.number === pageNumber.step1Content}
                        panelState={state}
                        setPanelState={setState}
                        setPage={setPage}
                        clean2Step={clean2Step}
                        initialPanelState={initialState}
                        updateState={updateState}
                        updateErrorState={updateErrorState}
                        panels={panels}
                        panelId={currentPanel?.id ?? null}
                        canSave={canSave}
                        isSysAdmin={isSysAdmin}
                    />
                    {page.params === 'line' && (
                        <Step2Line
                            ref={step2LineRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'line'}
                            page={page}
                            setPage={setPage}
                            setGraphSeriesState={setGraphSeriesState}
                            clearGraphSeriesState={clearGraphSeriesState}
                            panelState={state}
                            setPanelState={setState}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave || true}
                            scheduleOptions={getScheduleOptions()}
                        />
                    )}
                    {page.params === 'heatMap' && (
                        <Step2HeatMap
                            ref={step2HeatMapRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'heatMap'}
                            page={page}
                            setPage={setPage}
                            panelState={state}
                            setPanelState={setState}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                            scheduleOptions={getScheduleOptions()}
                        />
                    )}
                    {page.params === 'column' && (
                        <Step2Column
                            ref={step2ColumnRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'column'}
                            page={page}
                            setPage={setPage}
                            panelState={state}
                            setPanelState={setState}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                            scheduleOptions={getScheduleOptions()}
                        />
                    )}
                    {page.params === 'donut' && (
                        <Step2Donut
                            ref={step2DonutRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'donut'}
                            page={page}
                            setPage={setPage}
                            panelState={state}
                            setPanelState={setState}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {page.params === 'stackedColumn' && (
                        <Step2StackedColumn
                            ref={step2StackedColumnRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'stackedColumn'}
                            page={page}
                            setPage={setPage}
                            panelState={state}
                            setPanelState={setState}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave || true}
                            scheduleOptions={getScheduleOptions()}
                        />
                    )}

                    {page.params === 'triOperational' && (
                        <Step2TriOperationalModel
                            ref={step2TRI}
                            show={page.number === pageNumber.step2Setup && page.params === 'triOperational'}
                            panelState={state}
                            setPanelState={setState}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {(page.params === 'negativeBinomial' || page.params === 'poissonModel') && (
                        // poissonModel uses same config as negativeBinomial model, so uses same step2 component
                        <Step2NegativeBinomial
                            ref={step2NBRef}
                            show={
                                page.number === pageNumber.step2Setup &&
                                (page.params === 'negativeBinomial' || page.params === 'poissonModel')
                            }
                            panelState={state}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {page.params === 'funnelModel' && (
                        <Step2FunnelModel
                            ref={step2FunnelRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'funnelModel'}
                            panelState={state}
                            setPanelState={setState}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {page.params === 'poissonTrend' && (
                        <Step2PoissonTrend
                            ref={step2PoissonTrendRef}
                            show={page.number === pageNumber.step2Setup && page.params === 'poissonTrend'}
                            panelState={state}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {page.params === 'triOperationalTrendModel' && (
                        <Step2TriOperationalTrendModel
                            ref={step2TRITrend}
                            show={page.number === pageNumber.step2Setup && page.params === 'triOperationalTrendModel'}
                            panelState={state}
                            setPanelState={setState}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    {page.params === 'funnelTrendModel' && (
                        <Step2FunnelTrendModel
                            ref={step2FunnelTrendRef}
                            panelState={state}
                            show={page.number === pageNumber.step2Setup && page.params === 'funnelTrendModel'}
                            page={page}
                            setPage={setPage}
                            updateState={updateState}
                            updateErrorState={updateErrorState}
                            canSave={canSave}
                        />
                    )}
                    <Step3Preview
                        ref={thirdStepRef}
                        getPanelFromState={getPanelFromState}
                        panelState={state}
                        onClose={close}
                        studyId={studyId}
                        user={user}
                        page={page}
                        setPage={setPage}
                        updateState={updateState}
                        currentPanel={currentPanel}
                        reason={reason}
                        show={page.number === pageNumber.step3Preview}
                        showReasonForChangeDialog={showReasonForChangeDialog}
                        setLoadingState={disabledSave.setValue}
                        dashboardType={currentDashboardType}
                        siteId={siteId}
                    />
                    <Step4Overview
                        ref={fourthStepRef}
                        show={page.number === pageNumber.step4Overview}
                        canSave={canSave || true}
                        panelState={state}
                        updateState={updateState}
                        updateErrorState={updateErrorState}
                    />
                    <DialogActions
                        style={{ paddingTop: 20, paddingBottom: 10 }}
                        className={`add-panel-actions-wrapper-step ${
                            page.number < 2 && !currentPanel ? 'flex-end' : ''
                        }`}
                    >
                        {page.number === pageNumber.step1Content && <ModalUpdatedInfo item={currentPanel} />}
                        {page.number > pageNumber.step1Content && (
                            <Button
                                disabled={disabledSave.value || !canSave || pageHasErrors()}
                                text={getBackBtnText(page.number)}
                                id="back-button"
                                onClick={validateBackButton}
                            />
                        )}
                        <div>
                            {currentPanel &&
                                ((state.showStep4 && page.number < pageNumber.step4Overview) ||
                                    (!state.showStep4 && page.number < pageNumber.step3Preview)) && (
                                    <Button
                                        disabled={disabledSave.value || !canSave || pageHasErrors()}
                                        text="save"
                                        id="save-button"
                                        onClick={() => {
                                            if (canSave) onEditSave();
                                        }}
                                        style={{ marginRight: 10 }}
                                    />
                                )}

                            <Button
                                disabled={
                                    isSysAdmin ||
                                    disabledSave.value ||
                                    (page.number === pageNumber.step3Preview && !canSave) ||
                                    pageHasErrors()
                                }
                                text={proceedBtnText}
                                id={proceedBtnId}
                                onClick={onNextSaveClick}
                            />
                        </div>
                    </DialogActions>
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default AddPanelDialog;
